import type {Analytics} from '../../core/Analytics';
import {Event} from '../../enums/Event';
import {Player} from '../../enums/Player';
import {StreamTypes} from '../../enums/StreamTypes';
import {ErrorDetailBackend} from '../../features/errordetails/ErrorDetailBackend';
import {ErrorDetailTracking} from '../../features/errordetails/ErrorDetailTracking';
import type {Feature} from '../../features/Feature';
import type {FeatureConfig} from '../../features/FeatureConfig';
import type {CodecInfo} from '../../types/CodecInfo';
import type {FeatureConfigContainer} from '../../types/FeatureConfigContainer';
import type {PlaybackInfo} from '../../types/PlaybackInfo';
import type {QualityLevelInfo} from '../../types/QualityLevelInfo';
import {logger} from '../../utils/Logger';
import {isNotNullish, isNullish, tryJSONStringify} from '../../utils/Utils';
import {HTML5InternalAdapter} from '../html5/HTML5InternalAdapter';
import {HTMLVideoElementProxy} from '../html5/HTMLVideoElementProxy';
import type {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

import {ShakaPlayerProxy} from './ShakaPlayerProxy';
import {ShakaErrorEvent, ShakaPlayerEvent} from './shakaPlayerTypes';

export class ShakaInternalAdapter extends HTML5InternalAdapter implements InternalAdapterAPI {
  private readonly player: ShakaPlayerProxy;

  constructor(player: ShakaPlayerProxy) {
    const mediaElement = player.getMediaElement();
    super(mediaElement ? new HTMLVideoElementProxy(mediaElement as HTMLVideoElement) : undefined);
    this.player = player;
  }

  override initialize(analytics: Analytics): Array<Feature<FeatureConfigContainer, FeatureConfig>> {
    super.initialize(analytics);
    // we expect to receive the HTMLVideoElement from ShakaPlayer
    const videoElement = (this.player.getMediaElement() as HTMLVideoElement) ?? undefined;

    if (isNotNullish(videoElement)) {
      this.setMediaElement(new HTMLVideoElementProxy(videoElement));
    } else {
      // if we don't have video/media element from shaka player during initialization (shaka behaviour:
      // it has not loaded content yet), we need to wait for the `media-source` state change event and
      // then try to get the media element.
      const handleOnStateChangeAndSetMediaElement = (event: {state: string}) => {
        if (event.state === 'media-source') {
          try {
            this.player.removeEventListener(ShakaPlayerEvent.ONSTATECHANGE, handleOnStateChangeAndSetMediaElement);
            // we expect to receive the HTMLVideoElement from ShakaPlayer
            const mediaElement = this.player.getMediaElement() as HTMLVideoElement;
            this.setMediaElement(isNotNullish(mediaElement) ? new HTMLVideoElementProxy(mediaElement) : undefined);
          } catch (e) {
            logger.errorMessageToUser(
              'Something went wrong while getting underlying HTMLVideoElement. Not possible to attach adapter and initialize Bitmovin Analytics. Error: ',
              e,
            );
            this.release();
          }
        }
      };

      this.player.addEventListener(ShakaPlayerEvent.ONSTATECHANGE, handleOnStateChangeAndSetMediaElement);
    }

    // With shaka a qualitychange only triggers a buffering, so to catch a qualitychange in time, we need to check qualityinfo on bufferings as well.
    this.player.addEventListener(ShakaPlayerEvent.BUFFERING, (_) => {
      this.checkQualityLevelAttributes();
    });

    this.player.addEventListener(ShakaPlayerEvent.ONSTATECHANGE, (event: {state: string}) => {
      if (event.state === 'detach') {
        // Detach the player from the current media element. Leaves the player in a state where
        // it cannot play media, until it has been attached to something else.
        this.eventCallback(Event.UNLOAD, {});
        this.release();
      }
    });

    const errorDetailTracking = new ErrorDetailTracking(
      analytics.errorDetailTrackingSettingsProvider,
      new ErrorDetailBackend(analytics.errorDetailTrackingSettingsProvider.collectorConfig),
      [analytics.errorDetailSubscribable],
      undefined,
    );
    return [errorDetailTracking];
  }

  override getPlayerName = () => Player.SHAKA;

  getPlayerVersion = () => this.player.playerVersion ?? 'unknown';

  override isLive = () => {
    return this.player ? this.player.isLive() : undefined;
  };

  override getCurrentPlaybackInfo(): PlaybackInfo {
    const playbackInfo: PlaybackInfo = {
      ...super.getCurrentPlaybackInfo(),
    };

    const codecInfo = getShakaCodecInfo(this.player);
    this.sourceInfoFallbackService.applyAndStoreCodecInfo(playbackInfo, codecInfo);

    return playbackInfo;
  }

  override getStreamType(): StreamTypes | undefined {
    return getShakaStreamFormat(this.player);
  }

  override getStreamURL() {
    return this.player.getAssetUri() ?? undefined;
  }

  override getCurrentQualityLevelInfo(): QualityLevelInfo | null {
    const variantTracks = this.player.getVariantTracks();

    if (isNullish(variantTracks)) {
      // possibly can happen when player is destroyed
      return null;
    }

    const activeVideoTrack = variantTracks
      .filter((track: any) => track.active)
      .filter((track: any) => track.videoCodec || track.videoId !== undefined)[0];

    if (!activeVideoTrack) {
      // can only happen for audio-only streams
      return null;
    }

    const bitrate = activeVideoTrack.videoBandwidth || activeVideoTrack.bandwidth;
    const width = activeVideoTrack.width;
    const height = activeVideoTrack.height;

    return {
      bitrate: bitrate,
      width: width,
      height: height,
    };
  }

  /** override html5 adapter function to not listen to native media element "error" event,
   * but instead use shaka player "error" event
   */
  override registerMediaElementErrorEvent = (_: HTMLVideoElementProxy) => {
    this.player.addEventListener(ShakaPlayerEvent.ERROR, (event: {detail: ShakaErrorEvent}) => {
      this.eventCallback(Event.ERROR, {
        currentTime: this.mediaElement?.currentTime ?? 0,
        code: event.detail?.code ?? undefined,
        message: event.detail?.message ?? undefined,
        originalError: event,
        data: {
          additionalData: tryJSONStringify(event.detail),
          exceptionMessage: event.detail?.message ?? undefined,
          exceptionStacktrace: event.detail?.stack?.split('\n') ?? undefined,
        },
      });
    });
  };
}

export const getShakaCodecInfo = (player: any): CodecInfo | undefined => {
  const variantTracks = player.getVariantTracks();
  const activeTrack = variantTracks.find((track: any) => track.active);

  return {
    videoCodec: activeTrack?.videoCodec,
    audioCodec: activeTrack?.audioCodec,
  };
};

export const getShakaStreamFormat = (player: ShakaPlayerProxy): StreamTypes | undefined => {
  const manifestType = player.getManifestType()?.toLowerCase();

  if (manifestType === StreamTypes.dash) {
    return StreamTypes.dash;
  } else if (manifestType === StreamTypes.hls) {
    return StreamTypes.hls;
  }

  // https://shaka-player-demo.appspot.com/docs/api/shaka.Player.html#.LoadMode
  const LOAD_MODE_SRC_EQUALS = 3;

  // Fallback to progressive if no manifest type is available.
  // In order to avoid over-reporting of progressive when the source is not loaded yet
  // (where getManifestType is also null similar to when a progressive source is used)
  // we check that the load mode is SRC_EQUALS which indicates native player usage
  // and make sure that the asset is already loaded through check if the uri is available
  if (isNullish(manifestType) && player.getLoadMode() === LOAD_MODE_SRC_EQUALS && isNotNullish(player.getAssetUri())) {
    return StreamTypes.prog;
  }

  return undefined;
};
